import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { axiosNewInstance } from "../../config/Api";
import LangData from "../common/LangData";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const formatDate = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const PremiumHistory = () => {
    const history = useHistory();
    const token = localStorage.getItem("authToken");

    const user = useSelector((state) => state?.user);
    const loginUser = user?.loginuser;
    const mobile = loginUser?.mobile || "";
    const roleId = Number(loginUser?.role_id);

    // Initial policy list state
    const [initialLoading, setInitialLoading] = useState(false);
    const [policyList, setPolicyList] = useState([]);
    const [initialError, setInitialError] = useState(null);

    // Form/Search state (Shared/Employee)
    const [policyNo, setPolicyNo] = useState("");
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyResult, setHistoryResult] = useState(null);
    const [historyError, setHistoryError] = useState(null);

    // Modal state (Customer/Agent)
    const [showModal, setShowModal] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState("");

    const today = new Date();
    const fifteenDaysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 15);
    const [periodFrom, setPeriodFrom] = useState(fifteenDaysAgo);
    const [periodTo, setPeriodTo] = useState(today);

    const fetchInitialData = useCallback(async () => {
        if (!token) {
            history.push("/login");
            return;
        }

        // Only fetch policy list for roles 4 and 5
        if (roleId !== 4 && roleId !== 5) return;

        setInitialLoading(true);
        setInitialError(null);

        try {
            let agencyCode = "";
            const mobileNo = mobile.toString().trim().replace(/\D/g, "");

            if (roleId === 4) { // Agent
                const agentRes = await axiosNewInstance.get(`api/agentEnquiryLogin/${encodeURIComponent(mobileNo)}`, {
                    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
                });
                agencyCode = agentRes?.data?.agencyCode || "";
            }

            const isCustomer = roleId === 5;
            const endpoint = isCustomer ? "api/customerDetailsCustomer" : "api/customerDetails";
            const payload = isCustomer ? { referenceId: "5", mobileNo } : { referenceId: "1", agentId: agencyCode };

            const response = await axiosNewInstance.post(endpoint, payload, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
            });

            const details = response?.data?.policyDetails || response?.data?.policy_details || [];
            setPolicyList(Array.isArray(details) ? details : []);
        } catch (err) {
            setInitialError(err?.message || "Failed to fetch policy list.");
        } finally {
            setInitialLoading(false);
        }
    }, [token, mobile, roleId, history]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleOpenModal = (pNo) => {
        setSelectedPolicy(pNo);
        setHistoryResult(null);
        setHistoryError(null);
        setShowModal(true);
    };

    const fetchPremiumHistory = async (targetPolicy = null) => {
        const pNo = targetPolicy || (roleId === 2 ? policyNo : selectedPolicy);
        if (!pNo) {
            setHistoryError("Please enter/select a Policy Number.");
            return;
        }

        setHistoryLoading(true);
        setHistoryError(null);
        try {
            const response = await axiosNewInstance.post("api/premiumHistory", {
                policyNo: pNo.trim(),
                periodFrom: formatDate(periodFrom),
                periodTo: formatDate(periodTo),
                userId: "999999",
                userType: "Normal",
            }, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
            });
            setHistoryResult(response?.data ?? null);
        } catch (err) {
            const msg = err?.response?.data?.Message ||
                err?.response?.data?.message ||
                err?.message ||
                "Unable to fetch premium history.";
            setHistoryError(msg);
        } finally {
            setHistoryLoading(false);
        }
    };

    const renderHistoryTable = () => {
        if (!historyResult) return null;
        const records = historyResult?.Prem_hist || historyResult?.prem_hist || (Array.isArray(historyResult) ? historyResult : null);
        if (!Array.isArray(records) || records.length === 0) {
            return <div className="alert alert-info mt-3">{historyResult?.Message || historyResult?.message || "No records found."}</div>;
        }
        const columns = Object.keys(records[0]);
        return (
            <div className="table-responsive mt-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
                <table className="table table-sm text-left">
                    <thead>
                        <tr>
                            <th>#</th>
                            {columns.map((col) => <th key={col} style={{ textTransform: "none" }}>{col}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((row, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                {columns.map((col) => (
                                    <td key={col}>
                                        {typeof row[col] === "object" && row[col] !== null
                                            ? JSON.stringify(row[col])
                                            : String(row[col] ?? "")}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="container">
            <div className="row g-0">
                <div className="col-md-12 pr-2 mt-4">
                    <div className="card p-3 shadow-sm">
                        <h5 className="font-weight-bold fs-4 my-3 text-left">
                            <LangData keyname="premium_history" />
                        </h5>

                        {roleId === 2 ? (
                            <>
                                <div className="row mb-3 align-items-end p-2 bg-light rounded shadow-sm mx-1">
                                    <div className="col-md-3">
                                        <label className="font-weight-bold small">Policy Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Policy No"
                                            value={policyNo}
                                            onChange={(e) => setPolicyNo(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="font-weight-bold small">Period From</label>
                                        <DatePicker
                                            selected={periodFrom}
                                            onChange={(date) => setPeriodFrom(date)}
                                            dateFormat="yyyy-MM-dd"
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="font-weight-bold small">Period To</label>
                                        <DatePicker
                                            selected={periodTo}
                                            onChange={(date) => setPeriodTo(date)}
                                            dateFormat="yyyy-MM-dd"
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <button className="btn btn-primary btn-block" onClick={() => fetchPremiumHistory()} disabled={historyLoading}>
                                            {historyLoading ? "Searching..." : "Search"}
                                        </button>
                                    </div>
                                </div>
                                {historyError && <div className="alert alert-danger mt-3 py-2">{historyError}</div>}
                                {renderHistoryTable()}
                            </>
                        ) : initialLoading ? (
                            <div className="text-center p-5">
                                <span className="spinner-border spinner-border-lg" />
                                <p className="mt-2 text-muted">Loading policies...</p>
                            </div>
                        ) : initialError ? (
                            <div className="alert alert-danger">{initialError}</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Policy Number</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {policyList.length > 0 ? (
                                            policyList.map((policy, idx) => (
                                                <tr key={idx}>
                                                    <td>{idx + 1}</td>
                                                    <td>{policy.policyNo}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handleOpenModal(policy.policyNo)}
                                                        >
                                                            View Premium History
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="text-center text-muted">No policies found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Premium History Modal (For Customers/Agents) */}
            {showModal && (
                <>
                    <div className="modal-backdrop fade show" />
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-xl" role="document">
                            <div className="modal-content shadow-lg border-0">
                                <div className="modal-header bg-primary text-white">
                                    <h5 className="modal-title">Premium History - {selectedPolicy}</h5>
                                    <button type="button" className="close text-white" onClick={() => setShowModal(false)}>
                                        <span>&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="row mb-3 align-items-end p-2 bg-light rounded">
                                        <div className="col-md-4">
                                            <label className="font-weight-bold small">Policy Number (Uneditable)</label>
                                            <input type="text" className="form-control" value={selectedPolicy} readOnly disabled />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="font-weight-bold small">Period From</label>
                                            <DatePicker
                                                selected={periodFrom}
                                                onChange={(date) => setPeriodFrom(date)}
                                                dateFormat="yyyy-MM-dd"
                                                className="form-control"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="font-weight-bold small">Period To</label>
                                            <DatePicker
                                                selected={periodTo}
                                                onChange={(date) => setPeriodTo(date)}
                                                dateFormat="yyyy-MM-dd"
                                                className="form-control"
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <button className="btn btn-primary btn-block" onClick={() => fetchPremiumHistory()} disabled={historyLoading}>
                                                {historyLoading ? "Searching..." : "Search"}
                                            </button>
                                        </div>
                                    </div>

                                    {historyError && <div className="alert alert-danger py-2">{historyError}</div>}
                                    {renderHistoryTable()}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PremiumHistory;
